import { ShieldAlert, CreditCard, FileDown, AlertTriangle, Gavel, Clock } from "lucide-react";

// All supported modal types
export const MODAL_TYPE = {
  FORBIDDEN_SEARCH: "FORBIDDEN_SEARCH",
  INSUFFICIENT_UNLOCK: "INSUFFICIENT_UNLOCK",
  CSV_LIMIT: "CSV_LIMIT",
  UPGRADE_REQUIRED: "UPGRADE_REQUIRED",
  GENERIC_ERROR: "GENERIC_ERROR",
  INVALID_EMAIL: "INVALID_EMAIL",
  PASSWORD_TOO_COMMON: "PASSWORD_TOO_COMMON",
  SEARCH_LIMIT: "SEARCH_LIMIT",
  PAYMENT_LINKS: "PAYMENT_LINKS",
  NEW_TAKEDOWN_FEATURE: "NEW_TAKEDOWN_FEATURE",
  WHATS_NEW: "WHATS_NEW",
  TRIAL_ENDED: "TRIAL_ENDED",
  SECURITY_RESTRICTION: "SECURITY_RESTRICTION",
};

// Match API responses to modal types
export const ERROR_MATCHERS = [
  { type: MODAL_TYPE.FORBIDDEN_SEARCH, test: (r) => r.status === 403 || /forbidden search/i.test(r.message || "") },
  { type: MODAL_TYPE.INSUFFICIENT_UNLOCK, test: (r) => /insufficient unlock/i.test(r.message || "") },
  { type: MODAL_TYPE.CSV_LIMIT, test: (r) => /csv.*limit/i.test(r.message || "") || /export.*limit/i.test(r.message || "") },
  { type: MODAL_TYPE.UPGRADE_REQUIRED, test: (r) => /upgrade.*threat intel/i.test(r.message || "") },
];

// Modal content config for each type
export const MODAL_CONFIG = {
  [MODAL_TYPE.FORBIDDEN_SEARCH]: {
    title: "Restricted Search",
    description: "Access to this data is restricted under your current account level. If you are a related government entity, please contact us.",
    icon: <ShieldAlert className="h-5 w-5 text-sky-400" />,
    primary: { label: "Contact Us", href: "/contact" },
    secondary: { label: "Cancel" },
  },
  [MODAL_TYPE.INSUFFICIENT_UNLOCK]: {
    title: "Insufficient Unlock Credits",
    description: "You have no remaining unlock credits. Upgrade your license to continue.",
    icon: <CreditCard className="h-5 w-5 text-sky-400" />,
    primary: { label: "Upgrade", href: "/upgrades" },
    secondary: { label: "Cancel" },
  },

[MODAL_TYPE.PAYMENT_LINKS]: {
  title: "Payment Links Enabled",
  description:
    "Direct checkout is now available for your account. Visit the Upgrades page to complete your purchase securely.",
  icon: <CreditCard className="h-5 w-5 text-sky-400" />,
  primary: { label: "Upgrade", href: "/upgrades" },
  secondary: { label: "Cancel" },
},
[MODAL_TYPE.NEW_TAKEDOWN_FEATURE]: {
  title: "Managed Takedowns — now an add-on",
  eyebrow: "Whiteintel · New add-on",
  description: (
    <>
      <p className="text-neutral-300">
        Our analysts now file and follow up on registrar abuse reports for
        look-alike and phishing domains on your behalf — end-to-end, with
        evidence packaging and status tracking inside the dashboard.
      </p>
      <ul className="mt-4 space-y-2 text-[13px] text-neutral-300">
        <li className="flex gap-2.5">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
          <span>
            Available as an add-on for{" "}
            <span className="text-white font-medium">Enterprise</span> and{" "}
            <span className="text-white font-medium">Threat Intelligence</span>{" "}
            plans
          </span>
        </li>
        <li className="flex gap-2.5">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
          <span>
            Sold in 10-takedown packs; credits never expire while subscribed
          </span>
        </li>
        <li className="flex gap-2.5">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
          <span>
            Credits refunded on rejections from non-ICANN-compliant registrars
          </span>
        </li>
      </ul>
      <p className="mt-4 text-[13px] text-neutral-400">
        Read the announcement on our blog:{" "}
        <a
          href="https://whiteintel.io/blog/introducing-managed-takedown-services.html"
          target="_blank"
          rel="noreferrer"
          className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline font-medium"
        >
          Introducing Managed Takedown Services →
        </a>
      </p>
    </>
  ),
  icon: <Gavel className="h-5 w-5 text-sky-400" />,
  primary: { label: "View add-on", href: "/upgrades?tab=addons" },
  secondary: { label: "Maybe later" },
},
  [MODAL_TYPE.CSV_LIMIT]: {
    title: "CSV Export Limit Reached",
    description: "Your CSV export limit has been reached. Upgrade your plan or wait until the limit resets.",
    icon: <FileDown className="h-5 w-5 text-sky-400" />,
    primary: { label: "Upgrade Plan", href: "/upgrades" },
    secondary: { label: "Cancel" },
  },
  [MODAL_TYPE.UPGRADE_REQUIRED]: {
    title: "Upgrade Required",
    description: "IP or computer name searches require a Threat Intel tier subscription.",
    icon: <ShieldAlert className="h-5 w-5 text-sky-400" />,
    primary: { label: "Upgrade Now", href: "/upgrades" },
    secondary: { label: "Cancel" },
  },

    [MODAL_TYPE.SEARCH_LIMIT]: {
    title: "SEARCH LIMIT REACHED",
    description: "Your daily search limit is reached. Upgrade your account to have unlimited searches.",
    icon: <ShieldAlert className="h-5 w-5 text-sky-400" />,
    primary: { label: "Upgrade Now", href: "/upgrades" },
    secondary: { label: "Cancel" },
  },
    [MODAL_TYPE.INVALID_EMAIL]: {
    title: "INVALID EMAIL FORMAT",
    description: "Given query must be a valid email address.",
    icon: <AlertTriangle className="h-5 w-5 text-sky-400" />,
    primary: { label: "Close" },
  },
  [MODAL_TYPE.PASSWORD_TOO_COMMON]: {
    title: "PASSWORD TOO COMMON",
    description: "This password is too common to search. Please try a more specific password.",
    icon: <AlertTriangle className="h-5 w-5 text-sky-400" />,
    primary: { label: "Close" },
  },
  [MODAL_TYPE.GENERIC_ERROR]: {
    title: "An Error Occurred",
    description: "Something went wrong. Please try again later.",
    icon: <AlertTriangle className="h-5 w-5 text-sky-400" />,
    primary: { label: "Close" },
  },
  // Anti-scraping / automation-prevention response from the backend (HTTP 403
  // or 429). This is a usage-policy control on the web interface, NOT a security
  // incident on the user's account. Title/description are usually overridden at
  // render time; these are neutral fallbacks. Intentionally no icon — reads as
  // a measured policy notice. No "contact support" — the API is the supported
  // path for automation.
  [MODAL_TYPE.SECURITY_RESTRICTION]: {
    eyebrow: "Access Policy",
    title: "Automated Access Restricted",
    description:
      "Access to this resource has been temporarily suspended because activity from your session matched a pattern associated with automated requests. Automated access through the web interface is restricted to prevent large-scale data scraping and circumvention of API usage limits. This restriction will be lifted automatically within 6 hours.",
    icon: null,
    primary: { label: "Close" },
  },

  [MODAL_TYPE.TRIAL_ENDED]: {
    title: "Your trial period has ended",
    eyebrow: "Whiteintel · Trial",
    icon: <Clock className="h-5 w-5 text-sky-400" />,
    description: (
      <>
        <p className="text-neutral-300">
          Your evaluation of Whiteintel has ended. To continue access or discuss
          a paid plan, please reach out to our team.
        </p>
        <div className="mt-4 rounded-md border border-sky-900/40 bg-sky-500/[0.04] px-3 py-2.5 text-[12px] text-neutral-400">
          Please also check your inbox <span className="text-neutral-300">and spam folder</span> for emails from{" "}
          <span className="font-mono text-neutral-200">info@whiteintel.io</span>
          {" "}— our team may have already reached out about next steps.
        </div>
      </>
    ),
    primary: { label: "Contact us", href: "/contact" },
    secondary: { label: "Close" },
  },
};

// Given API response {status, message}, return modal type
export function resolveModalFromApi(r) {
  for (const m of ERROR_MATCHERS) {
    if (m.test(r)) return m.type;
  }
  return MODAL_TYPE.GENERIC_ERROR;
}
